import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL, SITE_NAME, DEFAULT_IMAGE, getSeoForPath, isNoindex, isSelfManaged, isKnownRoute } from '../seo/seoConfig';

// Renders document metadata for the current route using React 19 native
// metadata hoisting. Pages under self managed prefixes (e.g. /blog) own
// their own metadata, and unknown routes (404) are left to the NotFound
// page, so this stays silent to avoid duplicate / conflicting tags.
const RouteSeo = () => {
  const { pathname } = useLocation();

  // Remove the static fallback <title> from index.html once React has taken
  // over metadata, so there is never a duplicate title in the document head.
  useEffect(() => {
    const fallback = document.getElementById('app-fallback-title');
    if (fallback) fallback.remove();
  }, []);

  if (isSelfManaged(pathname)) return null;
  if (!isKnownRoute(pathname)) return null;

  const { title, description, keywords } = getSeoForPath(pathname);
  const canonical = SITE_URL + (pathname === '/' ? '' : pathname.replace(/\/$/, ''));
  const noindex = isNoindex(pathname);
  const kw = Array.isArray(keywords) ? keywords.join(', ') : keywords;
  const isHome = pathname === '/' || pathname === '/home';

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description: 'A contemplative learning platform for critical thinking, cognitive biases, rhetoric and public speaking.',
  };
  const siteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/blog?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
  // Primary navigation, declared so search engines prefer these as sitelinks.
  const navLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'BWGELA primary navigation',
    itemListElement: [
      ['Home', ''],
      ['Courses', '/modules'],
      ['Blog', '/blog'],
      ['Biases', '/biases'],
      ['Rhetoric', '/rhetoric'],
      ['Kinesics', '/kinesics'],
      ['Public Speaking', '/public-speaking'],
      ['Resources', '/resources'],
    ].map(([name, path], i) => ({
      '@type': 'SiteNavigationElement',
      position: i + 1,
      name,
      url: SITE_URL + path,
    })),
  };

  const crumbParts = pathname.split('/').filter(Boolean);
  const isCourse = crumbParts[0] === 'courses' || crumbParts[0] === 'learning-path';
  const courseName = title.split(' | ')[0];
  const courseLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: courseName,
    description,
    url: canonical,
    inLanguage: 'en-GB',
    isAccessibleForFree: true,
    educationalLevel: (pathname.match(/pre-intermediate|intermediate|advanced|beginner|masters/) || ['All levels'])[0].replace(/-/g, ' '),
    provider: { '@type': 'Organization', name: SITE_NAME, sameAs: SITE_URL },
    offers: { '@type': 'Offer', price: 0, priceCurrency: 'GBP', category: 'Free' },
    hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'online', courseWorkload: 'PT6H' },
  };
  const crumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [['Home', ''], ['Courses', '/modules'], [courseName, pathname]].map(([name, p], i) => ({ '@type': 'ListItem', position: i + 1, name, item: SITE_URL + p })),
  };

  return (
    <>
      {isCourse && !noindex ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(courseLd) }} /> : null}
      {isCourse && !noindex ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(crumbLd) }} /> : null}
      <title>{title}</title>
      <meta name="description" content={description} />
      {kw ? <meta name="keywords" content={kw} /> : null}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
      {isHome ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }} /> : null}
      {isHome ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(siteLd) }} /> : null}
      {isHome ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(navLd) }} /> : null}
    </>
  );
};

export default RouteSeo;
